import React, {useState, useEffect, useContext} from 'react';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import {NewProjectContext} from '../utils/NewProjectContext';
import ActionButtons from './ActionButtons';
import {CUSTOM_EVENT_NAMES, fireCustomEvent} from '../../../util/Analytics';
import {
  resetSecuredFetchRequest,
  securedFetchRequest,
} from '@bidmii/common/lib/util/FetchRequest';
import {userState} from '../../../recoil/user/userAtom';
import {useRecoilValue} from 'recoil';
import * as Sentry from '@sentry/react';
import {API_URL} from '../../../constants';
import BidmiiCheckbox from '../../../common/StyledComponents/BidmiiCheckbox';
import {firebaseAuth} from '../../../App';
import {signInAnonymously} from 'firebase/auth';
import {apiRootState} from '../../../recoil/rootAtom';

interface IProps {
  changeStepHandler: (number: number) => void;
  forward: boolean;
}

const ProjectPriorities = ({changeStepHandler, forward}: IProps) => {
  useEffect(() => {
    fireCustomEvent(CUSTOM_EVENT_NAMES.NEW_PROJECT_WIZARD_PRIORITIES);
  }, []);

  const user = useRecoilValue(userState);
  const {project, setProject, template, setTemplate, setNewProjectStep} =
    useContext(NewProjectContext);

  const apiRoot = useRecoilValue(apiRootState);
  const [loading, setLoading] = useState(false);
  const [initProjectLoading, setInitProjectLoading] = useState(true);
  const [readyToMoveForward, setReadyToMoveForward] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [priorityIsCost, setPriorityIsCost] = useState<boolean>(false);
  const [priorityIsQuality, setPriorityIsQuality] = useState<boolean>(false);
  const [priorityIsSpeed, setPriorityIsSpeed] = useState<boolean>(false);

  const savedDetail =
    project.details &&
    project.details.find(
      (detail: $TSFixMe) => detail.prompt === 'Top project priorities are:',
    );

  useEffect(() => {
    if (!initProjectLoading && readyToMoveForward) {
      changeStepHandler(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initProjectLoading, readyToMoveForward]);

  const initProject = (anonymous: boolean) => {
    setInitProjectLoading(true);
    const rfp: $TSFixMe = {
      enabled: true,
      title: project.title as string,
      description: project.description || '',
      privacy: 'anyone',
    };

    if (anonymous) {
      rfp.enabled = false;
    }
    process.env.NODE_ENV === 'development' && console.log('rfp :>> ', rfp);

    // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
    return securedFetchRequest(apiRoot._links.rfps.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(rfp),
    })
      .then((res) => res.json())
      .then((res) => {
        localStorage.setItem('rfp', JSON.stringify(res));
        setProject(res);
        initProjectTemplate();
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.error(err);

        Sentry.withScope(function (scope) {
          scope.setUser({
            uid: user?.uid,
            firstName: user?.firstName,
          });
          scope.setTag('section', 'create_user_step_2');
          Sentry.captureException(err);
        });
      });
  };

  const initProjectTemplate = () => {
    if (template && Object.keys(template).length > 0) {
      return;
    }

    setInitProjectLoading(true);
    setLoading(true);

    return securedFetchRequest(
      `${API_URL}/dynamic-project-template?title=${encodeURIComponent(
        project.title as string,
      )}`,
    )
      .then((res) => {
        return res.status !== 200 ? setTemplate({}) : res.json();
      })
      .then((res) => {
        console.log('template :>> ', res);
        setInitProjectLoading(false);
        setLoading(false);
        setTemplate(res);
      })
      .catch((err) => {
        setInitProjectLoading(false);
        setLoading(false);
        setTemplate({});
        process.env.NODE_ENV === 'development' && console.error(err);

        Sentry.withScope(function (scope) {
          scope.setUser({
            uid: user?.uid,
            firstName: user?.firstName,
          });
          scope.setTag('section', 'create-project-template');
          Sentry.captureException(err);
        });
      });
  };

  const loginAnonymously = () => {
    signInAnonymously(firebaseAuth)
      .catch(function (err) {
        process.env.NODE_ENV === 'development' &&
          console.error('ANONYMOUS LOGIN FAIL');
        process.env.NODE_ENV === 'development' && console.error(err.code);
        process.env.NODE_ENV === 'development' && console.error(err.message);
        Sentry.withScope(function (scope) {
          scope.setTag('section', 'NewProjectStepOne.tsx loginAnonymously()');
          Sentry.captureException(err);
        });
      })
      .then((res) => {
        localStorage.setItem('isAnonymous', '1');
        localStorage.setItem('anonymousUid', (res as $TSFixMe).user?.uid);
        localStorage.setItem('userType', 'REQUESTER');
      });
  };

  useEffect(() => {
    if (!apiRoot) return;
    if (!user && !project.uid && apiRoot) {
      firebaseAuth.onAuthStateChanged((updatedUser) => {
        resetSecuredFetchRequest(
          updatedUser
            ? (forceRefresh) => updatedUser.getIdToken(forceRefresh)
            : null,
        );
        if (updatedUser !== null && updatedUser.isAnonymous) {
          initProject(true);
        }
      });

      loginAnonymously();
    } else if (!project.uid) {
      initProject(false);
    } else if (
      (project.uid && !template) ||
      Object.keys(template).length === 0
    ) {
      initProjectTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRoot]);

  const getPrioritySummary = () => {
    let summary = '';
    if (priorityIsCost) {
      summary += 'Cost';
    }
    if (priorityIsQuality) {
      summary += summary === '' ? 'Quality' : ' and Quality';
    }
    if (priorityIsSpeed) {
      summary += summary === '' ? 'Speed' : ' and Speed';
    }

    return summary;
  };

  const handleClickNext = () => {
    setLoading(true);
    setDisabled(true);
    const body = {
      value: getPrioritySummary(),
      type: 'SHORT_FORM_TEXT',
      prompt: 'Top project priorities are:',
    };

    const url = !savedDetail
      ? `${API_URL}/rfps/${project.uid}/details`
      : `${API_URL}/rfps/details/${savedDetail?.id}`;
    securedFetchRequest(url, {
      method: !savedDetail ? 'POST' : 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((res) => res.json())
      .then((res) => {
        setLoading(false);
        localStorage.setItem('rfp', JSON.stringify(res));
        setProject(res);
        setReadyToMoveForward(true);
      })
      .catch((err) => {
        setLoading(false);
        setReadyToMoveForward(true);
        process.env.NODE_ENV === 'development' && console.error(err);

        Sentry.withScope(function (scope) {
          scope.setUser({
            uid: user?.uid,
            firstName: user?.firstName,
          });
          scope.setTag('section', 'create_user_step_2');
          Sentry.captureException(err);
        });
      });
  };

  return (
    <Slide in={true} direction={forward ? 'left' : 'right'} timeout={400}>
      <div style={{width: '100%', maxWidth: 450}}>
        <Typography variant="h6" fontSize={22} mb={3} fontWeight="bold">
          What is most important to you for this project? (Choose up to two)
        </Typography>
        <BidmiiCheckbox
          label={'Keeping the cost down'}
          checked={priorityIsCost}
          disabled={priorityIsSpeed && priorityIsQuality}
          onChange={() => setPriorityIsCost((prevState) => !prevState)}
        />
        <BidmiiCheckbox
          label={'Completing it quickly'}
          checked={priorityIsSpeed}
          disabled={priorityIsCost && priorityIsQuality}
          onChange={() => setPriorityIsSpeed((prevState) => !prevState)}
        />
        <BidmiiCheckbox
          label={'Achieving the highest quality'}
          checked={priorityIsQuality}
          disabled={priorityIsCost && priorityIsSpeed}
          onChange={() => setPriorityIsQuality((prevState) => !prevState)}
        />
        <ActionButtons
          secondaryAction={() => setNewProjectStep(0)}
          primaryAction={handleClickNext}
          disabled={disabled}
          loading={loading}
        />
      </div>
    </Slide>
  );
};

export default ProjectPriorities;
